import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineBriefcase } from "react-icons/hi";
import WorkUnitsFilters from "../../../pages/Projects/sections/WorkUnitsFilters";
import "./ListWorkUnits.css";
import AddUpdateProjectModal from "../../../pages/Projects/sections/AddUpdateProjectModal";
import CreateAdvancedEpicModal from "../../AdvancedEpic/CreateAdvancedEpicModal";
import Portal from "../../../components/Portal";
import { useOrganizationService } from "../../../services";
import { CombinedItem } from "../../../pages/Projects/roadmapsAndProjects.types";
import { setSingleRoadmap } from "../../../redux/slices/roadmap";
import WorkUnitsTableView from "./components/WorkUnitsTableView";
import WorkUnitsGridView from "./components/WorkUnitsGridView";
import { motion, AnimatePresence } from "framer-motion";

interface ListWorkUnitsProps {
  showTable: boolean;
  scrollProgress: number;
  originalFilterOpacity: number;
  viewType: "list" | "grid";
  filterVisibility: boolean;
  setViewType: (type: "list" | "grid") => void;
  visibleColumns: Record<string, boolean>;
  setVisibleColumns: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  projectData: any[]; // Consider using proper type
  onFilteredDataChange: (data: any[]) => void;
  totalItems: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  initialLoading: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
  searchType: "all" | "roadmap" | "project" | "epic" | "feature";
  setSearchType: (type: "all" | "roadmap" | "project" | "epic" | "feature") => void;
  sortBy: any;
  setSortBy: (sort: any) => void;
  sourceFilter: string;
  setSourceFilter: (filter: string) => void;
  statusFilter: string[];
  setStatusFilter: (filter: string[]) => void;
  pinnedFilter: boolean;
  setPinnedFilter: (pinned: boolean) => void;
  quarterFilter: string[];
  setQuarterFilter: (quarter: string[]) => void;
  filteredProjectData: any[];
  handleProjectClick: (data?: any, toggleTable?: boolean) => void;
  visibleColumnsForList: Record<string, boolean>;
  isLocalFilterApplied: boolean;
  tableScrollRef: React.RefObject<HTMLDivElement>;
  gridScrollRef: React.RefObject<HTMLDivElement>;
  gridContainerRef?: React.RefObject<HTMLDivElement>;
  filterSectionRef: React.RefObject<HTMLDivElement>;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}

const ListWorkUnits: React.FC<ListWorkUnitsProps> = ({
  showTable,
  scrollProgress,
  originalFilterOpacity,
  viewType,
  setViewType,
  visibleColumns,
  setVisibleColumns,
  projectData,
  onFilteredDataChange,
  totalItems,
  currentPage,
  totalPages,
  loading,
  initialLoading,
  loadingMore,
  onLoadMore,
  hasMore,
  searchType,
  setSearchType,
  sortBy,
  setSortBy,
  sourceFilter,
  setSourceFilter,
  statusFilter,
  setStatusFilter,
  pinnedFilter,
  setPinnedFilter,
  quarterFilter,
  setQuarterFilter,
  filteredProjectData,
  handleProjectClick,
  visibleColumnsForList,
  isLocalFilterApplied,
  tableScrollRef,
  gridScrollRef,
  gridContainerRef,
  filterSectionRef,
  filterVisibility,
  hasActiveFilters = false,
  onClearFilters,
}) => {
  const dispatch = useDispatch();
  const { fetchRoadmapsAndProjects, fetchPinnedRoadmapsAndProjects } =
    useOrganizationService();

  // Get current workItems filters from Redux to preserve them when reloading
  const {
    workItemsSearchTerm,
    workItemsSearchType,
    workItemsSortBy,
    workItemsSourceFilter,
    workItemsQuarterFilter = [],
    workItemsPinnedFilter = false,
  } = useSelector((state: any) => state.organization);

  // Edit modals state
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showRoadmapModal, setShowRoadmapModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>({});
  const [selectedRoadmap, setSelectedRoadmap] = useState<any>({});

  const handleEdit = (data: CombinedItem) => {
    if (data.type === "roadmap") {
      // Epic Advance - use CreateRoadMapsModal
      setSelectedRoadmap({ ...data });
      setShowRoadmapModal(true);
    } else {
      // Normal project - use AddUpdateProjectModal
      const projectData = {
        ...data,
        project_id: data.id,
        project_name: data.name,
        project_description: data.description,
        project_color: data.color,
      };
      setSelectedProject(projectData);
      setShowProjectModal(true);
    }
  };

  const handleRoadmapModalClose = (val: boolean) => {
    setShowRoadmapModal(val);
    if (!val) {
      setSelectedRoadmap({});
      dispatch(setSingleRoadmap({}));
    }
  };

  const handleProjectModalClose = (val: boolean) => {
    setShowProjectModal(val);
    if (!val) {
      setSelectedProject({});
    }
  };

  // Smart pagination: track how many filtered items we want visible
  const MIN_PAGE_SIZE = 20;
  const [minVisibleCount, setMinVisibleCount] = useState(MIN_PAGE_SIZE);

  // Reset target when status filter changes
  useEffect(() => {
    setMinVisibleCount(MIN_PAGE_SIZE);
  }, [statusFilter]);

  // Auto-load more pages until we have enough filtered items to meet the threshold
  useEffect(() => {
    if (!hasMore || loadingMore || loading || initialLoading) return;
    // Only auto-load when a status filter is active
    if (!statusFilter || statusFilter.length === 0) return;
    if (filteredProjectData.length >= minVisibleCount) return;

    const timer = setTimeout(() => onLoadMore(), 300);
    return () => clearTimeout(timer);
  }, [filteredProjectData.length, minVisibleCount, hasMore, loadingMore, loading, initialLoading, onLoadMore, statusFilter]);

  // When user scrolls to bottom, increase the target so more pages auto-load
  const handleScrollLoadMore = useCallback(() => {
    if (statusFilter && statusFilter.length > 0) {
      // With status filter: increase threshold to trigger auto-load
      setMinVisibleCount((prev) => prev + MIN_PAGE_SIZE);
    } else {
      // Without status filter: normal pagination
      onLoadMore();
    }
  }, [statusFilter, onLoadMore]);

  // True when auto-load is still fetching pages for the status filter
  const isAutoLoading = !!(statusFilter && statusFilter.length > 0 && hasMore && filteredProjectData.length < minVisibleCount);
  // Show full skeletons only when NO filtered data exists yet; otherwise show data + bottom loader
  const isAutoLoadingEmpty = isAutoLoading && filteredProjectData.length === 0;
  const isAutoLoadingMore = isAutoLoading && filteredProjectData.length > 0;

  if (!showTable) return null;

  return (
    <section
      ref={filterSectionRef}
      className={`px-6`}
      data-onboarding-id="project-list"
    >
      {/* Section Title - Fades out */}

      <AnimatePresence>
        {filterVisibility && (
          <motion.div
            key="filter-container"
            exit={{ height: "0", opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="filter-container overflow-hidden"
          >
            <div
              className="flex items-center gap-2 mb-4 "
              style={{
                opacity: Math.max(0, 1 - scrollProgress * 1.8),
                transition: "opacity 0.15s ease-out",
              }}
            >
              <HiOutlineBriefcase className="w-5 h-5 text-slate-400 dark:text-slate-500" />
              <h2 className="text-base font-semibold text-light-text-primary dark:text-white">
                All Work Items
              </h2>
            </div>

            {/* Original Filters - Fade out as header filters appear */}
            <div
              style={{
                opacity: originalFilterOpacity,
                transition: "opacity 0.2s ease-out",
              }}
            >
              {
                <WorkUnitsFilters
                  viewType={viewType}
                  setViewType={setViewType}
                  visibleColumns={visibleColumns}
                  setVisibleColumns={setVisibleColumns}
                  projectData={projectData}
                  onFilteredDataChange={onFilteredDataChange}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  loading={loading || initialLoading}
                  tableScrollRef={tableScrollRef}
                  gridScrollRef={gridScrollRef}
                  isCompact={false}
                  searchType={searchType}
                  setSearchType={setSearchType}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  sourceFilter={sourceFilter}
                  setSourceFilter={setSourceFilter}
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                  pinnedFilter={pinnedFilter}
                  setPinnedFilter={setPinnedFilter}
                  quarterFilter={quarterFilter}
                  setQuarterFilter={setQuarterFilter}
                  loadingMore={loadingMore || isAutoLoadingMore}
                  hasActiveFilters={hasActiveFilters}
                  onClearFilters={onClearFilters}
                />
              }
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table/Grid content */}
      {viewType === "list" ? (
        <motion.div
          layout
          initial={{ opacity: 0, y: filterVisibility ? 20 : 0 }}
          animate={{
            opacity: 1,
            y: filterVisibility ? 20 : 0,
            height: !filterVisibility ? "70vh" : "",
          }}
          transition={{ duration: 0.3 }}
          className={`projects_table list-work-units-container  overflow-hidden `}
        >
          <WorkUnitsTableView
            data={filteredProjectData}
            handleProjectClick={handleProjectClick}
            loading={loading || initialLoading || isAutoLoadingEmpty}
            loadingMore={loadingMore || isAutoLoadingMore}
            onLoadMore={handleScrollLoadMore}
            hasMore={hasMore}
            visibleColumns={visibleColumnsForList}
            setVisibleColumns={setVisibleColumns}
            isLocalFilterApplied={isLocalFilterApplied}
            scrollRef={tableScrollRef}
          />
        </motion.div>
      ) : (
        <motion.div
          ref={gridContainerRef as any}
          layout
          initial={{ opacity: 0, y: 0 }}
          animate={{
            opacity: 1,
            y: filterVisibility ? 20 : 0,
            height: !filterVisibility ? "calc(100vh - 228px)" : "",
          }}
          transition={{ duration: 0.3, ease: "easeInOut", delay: 0.1 }}
          className={`list-work-units-container  overflow-scroll relative`}
          onScroll={(e: any) => {
            const target = e.target as HTMLDivElement;
            const bottom =
              Math.abs(
                target.scrollHeight - target.scrollTop - target.clientHeight,
              ) < 50;
            if (
              bottom &&
              hasMore &&
              !loadingMore &&
              !loading &&
              !initialLoading
            ) {
              handleScrollLoadMore();
            }
          }}
        >
          <WorkUnitsGridView
            data={filteredProjectData}
            handleProjectClick={handleProjectClick}
            handleEdit={handleEdit}
            loading={loading || isAutoLoadingEmpty}
            initialLoading={initialLoading}
            loadingMore={loadingMore || isAutoLoadingMore}
            onLoadMore={handleScrollLoadMore}
            hasMore={hasMore}
            scrollRef={gridScrollRef}
          />
        </motion.div>
      )}

      {/* Edit Project Modal */}
      {showProjectModal && (
        <Portal>
          <AddUpdateProjectModal
            isVisible={showProjectModal}
            setShowModal={handleProjectModalClose}
            project={selectedProject}
            onProjectUpdate={() => {
              // Only refresh when there's an actual update - preserve filters
              fetchRoadmapsAndProjects(
                true,
                undefined,
                workItemsSearchTerm,
                workItemsSearchType,
                workItemsSortBy,
                workItemsSourceFilter,
                workItemsQuarterFilter,
                workItemsPinnedFilter,
              );
              fetchPinnedRoadmapsAndProjects();
            }}
          />
        </Portal>
      )}

      {/* Edit Roadmap Modal */}
      {showRoadmapModal && (
        <Portal>
          <CreateAdvancedEpicModal
            isVisible={showRoadmapModal}
            setShowModal={handleRoadmapModalClose}
            roadmap={selectedRoadmap}
            setSelectedRoadmap={async () => {
              fetchRoadmapsAndProjects(
                true,
                undefined,
                workItemsSearchTerm,
                workItemsSearchType,
                workItemsSortBy,
                workItemsSourceFilter,
                workItemsQuarterFilter,
                workItemsPinnedFilter,
              );
              fetchPinnedRoadmapsAndProjects();
            }}
          />
        </Portal>
      )}
    </section>
  );
};

export default ListWorkUnits;
